import { useEffect, useState } from "react";
import ReusableTable from "../ReusableTable/ReusableTable";
import classes from "./AccreditationB.module.css";
import axios from "axios";
import { env } from "../../constants/env";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../utils/offlineDB";

const AccreditationB = () => {
  const { token, user } = useAuth();
  const userRole = user?.role;

  const [labData, setLabData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sectionColumns = [
    { label: "Date & time entered", field: "date" },
    { label: "Laboratory name", field: "laboratoryName" },
    { label: "HPCZ License number", field: "licenseNumber" },
    { label: "Physical address", field: "address" },
    { label: "Facility phone", field: "phone" },
    { label: "Facility Email", field: "email" },
  ];

  useEffect(() => {
    const fetchLabs = async () => {
      setLoading(true);
      try {
        // 1️⃣ Load local labs first
        let localLabs = await db.offlineLabs.toArray();

        // Filter for ASSESSOR accessible labs
        if (userRole === "ASSESSOR") {
          localLabs = localLabs.filter((lab) =>
            user.accessibleLabs?.includes(lab.id)
          );
        }

        if (localLabs.length > 0) {
          const unsyncedLabs = localLabs.filter(
            (lab) => !lab.lastSynced || new Date(lab.timestamp) > new Date(lab.lastSynced)
          );
          if (unsyncedLabs.length > 0) {
            console.log("Using newer local SADCAS B labs data");
            const formattedLocal = localLabs.map((lab) => ({
              date: new Date(lab.createdAt || lab.timestamp).toLocaleString(),
              laboratoryName: lab.labName || "",
              licenseNumber: lab.hpczLicense || "",
              address: lab.physicalAddress || "",
              phone: lab.telephone || "",
              email: lab.email || "",
              id: lab.id,
            }));
            setLabData(formattedLocal);
            setLoading(false);
            return;
          }
        }

        // 2️⃣ Offline: use local data
        if (!navigator.onLine) {
          if (localLabs.length > 0) {
            const formattedLocal = localLabs.map((lab) => ({
              date: new Date(lab.createdAt || lab.timestamp).toLocaleString(),
              laboratoryName: lab.labName || "",
              licenseNumber: lab.hpczLicense || "",
              address: lab.physicalAddress || "",
              phone: lab.telephone || "",
              email: lab.email || "",
              id: lab.id,
            }));
            setLabData(formattedLocal);
          } else {
            setError("No offline data available");
          }
          setLoading(false);
          return;
        }

        // 3️⃣ Online: fetch from API
        let serverLabs = (await axios.get(`${env.kataApiUrl}/api/labs`, {
          headers: { Authorization: `Bearer ${token}` },
        })).data;

        // Filter for ASSESSOR accessible labs
        if (userRole === "ASSESSOR") {
          serverLabs = serverLabs.filter((lab) =>
            user.accessibleLabs?.includes(lab._id)
          );
        }

        const formatted = serverLabs.map((lab) => ({
          date: new Date(lab.createdAt).toLocaleString(),
          laboratoryName: lab.labName || "",
          licenseNumber: lab.hpczLicense || "",
          address: lab.physicalAddress || "",
          phone: lab.telephone || "",
          email: lab.email || "",
          id: lab._id,
        }));

        setLabData(formatted);

        // Save/update IndexedDB
        const savePromises = serverLabs.map((lab) =>
          db.offlineLabs.put({
            ...lab,
            id: lab._id,
            offline: false,
            lastSynced: new Date().toISOString(),
            timestamp: Date.now(),
          })
        );
        await Promise.all(savePromises);
      } catch (err) {
        console.error("Error fetching SADCAS B labs:", err);
        if (!labData.length) setError("Failed to fetch SADCAS B labs");
      } finally {
        setLoading(false);
      }
    };

    fetchLabs();
  }, [token, userRole, user?.accessibleLabs]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!labData || labData.length === 0) return <p>No SADCAS B labs found</p>;

  return (
    <div className={classes.wrapper}>
      <ReusableTable
        title="SADCAS - F134  [B]"
        columns={sectionColumns}
        data={labData}
        detailsLink="lab-details"
        accreditationName="SADCAS F134-B"
      />
    </div>
  );
};

export default AccreditationB;